import { IcoBook16, IcoCode16, IcoCog16, IcoDatabase16, IcoLayer0116 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SuggestionListHandle } from '@/components/mention-suggestion';
import type { PromptMentionItem, PromptMentionKind } from './prompt-mention-items';
const ICON_BY_KIND: Record<PromptMentionKind, typeof IcoLayer0116> = {
  task: IcoLayer0116,
  input: IcoDatabase16,
  output: IcoCode16,
  tool: IcoCog16,
  skill: IcoBook16
};
const SECTION_ORDER: PromptMentionKind[] = ['task', 'input', 'output', 'tool', 'skill'];
type PromptMentionListProps = {
  items: PromptMentionItem[];
  query: string;
  onSelect: (item: PromptMentionItem) => void;
};
const PromptMentionList = forwardRef<SuggestionListHandle, PromptMentionListProps>(({
  items,
  query,
  onSelect
}, ref) => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder.mention'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const isKeyboardNav = useRef(false);
  const sections = (() => {
    let offset = 0;
    return SECTION_ORDER.flatMap(kind => {
      const sectionItems = items.filter(item => item.kind === kind);
      if (sectionItems.length === 0) return [];
      const section = {
        kind,
        items: sectionItems,
        offset
      };
      offset += sectionItems.length;
      return [section];
    });
  })();
  const flatList = sections.flatMap(s => s.items);
  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);
  useEffect(() => {
    if (!isKeyboardNav.current) return;
    isKeyboardNav.current = false;
    const el = containerRef.current?.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | undefined;
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [selectedIndex]);
  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        isKeyboardNav.current = true;
        setSelectedIndex(prev => prev <= 0 ? flatList.length - 1 : prev - 1);
        return true;
      }
      if (event.key === 'ArrowDown') {
        isKeyboardNav.current = true;
        setSelectedIndex(prev_0 => (prev_0 + 1) % Math.max(flatList.length, 1));
        return true;
      }
      // Tab completes like Enter — plain Tab only, so Shift+Tab keeps its focus-navigation meaning.
      if (event.key === 'Enter' || event.key === 'Tab' && !event.shiftKey) {
        const item_0 = flatList[selectedIndex];
        if (item_0) {
          onSelect(item_0);
          return true;
        }
        return false;
      }
      return false;
    },
    // Items come straight from builder state — there's never an async search to wait on.
    setLoading: () => {}
  }));
  const sectionLabel = (kind_0: PromptMentionKind): string => {
    if (kind_0 === 'task') return t($ => $.sections.tasks);
    if (kind_0 === 'input') return t($_0 => $_0.sections.inputs);
    if (kind_0 === 'output') return t($_1 => $_1.sections.outputs);
    if (kind_0 === 'skill') return t($_2 => $_2.sections.skills);
    return t($_3 => $_3.sections.tools);
  };
  if (flatList.length === 0) {
    return <div className="w-fit min-w-[260px] bg-primary border border-tertiary rounded-default shadow-md p-1">
          <div className="px-2 py-1.5 text-body-3 text-tertiary">
            {query.trim() ? t($_4 => $_4.noMatches, {
          query
        }) : t($_5 => $_5.empty)}
          </div>
        </div>;
  }
  const renderItem = (item_1: PromptMentionItem, index: number) => {
    const isSelected = index === selectedIndex;
    const Icon = ICON_BY_KIND[item_1.kind];
    return <button key={item_1.key} type="button" data-index={index} className={cx('flex items-center gap-2 w-full px-2 py-1.5 rounded-default text-left cursor-pointer', isSelected ? 'bg-(--dropdown-hover-bg)' : 'hover:bg-(--dropdown-hover-bg)')} onClick={() => onSelect(item_1)} onMouseEnter={() => setSelectedIndex(index)}>
          <span className="shrink-0 flex items-center text-tertiary">
            <Icon />
          </span>
          <span className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className={cx('text-body-3 text-primary break-words', (item_1.kind === 'output' || item_1.kind === 'input') && 'font-mono')}>
              {item_1.label}
            </span>
            {item_1.sublabel && <span className="text-snippet-3 text-tertiary truncate">{item_1.sublabel}</span>}
          </span>
        </button>;
  };
  return <div className="w-fit min-w-[260px] max-w-[calc(100vw-32px)] md:max-w-[480px] bg-primary border border-tertiary rounded-default shadow-md p-1 max-h-[320px] overflow-y-auto flex flex-col gap-3">
        <div ref={containerRef} className="flex flex-col gap-3">
          {sections.map(section_0 => <div key={section_0.kind} className="flex flex-col gap-0.5">
              <div className="px-2 pt-2 pb-1 text-tertiary text-caption-3">{sectionLabel(section_0.kind)}</div>
              {section_0.items.map((item_2, i) => renderItem(item_2, section_0.offset + i))}
            </div>)}
        </div>
      </div>;
});
export default PromptMentionList;